import { supabase } from '@/integrations/supabase/client';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export interface VisitRecord {
  id: string;
  company_id: string;
  customer_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  duration_sec?: number | null;
  source: 'manual' | 'face_recognition' | 'qr_scan';
  temporary: boolean;
  visit_type?: string | null;
  notes?: string | null;
  location?: string | null;
  metadata?: Record<string, unknown>;
  status?: string | null;
  created_at: string;
  updated_at: string;
  customer?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
}

export interface CreateVisitData {
  customer_id?: string | null;
  started_at: string;
  ended_at?: string; // Optional ended_at for manual visits
  source: string;
  temporary?: boolean;
  visit_type?: string;
  product_id?: string;
  notes?: string;
  photos?: string[];
  reminder_date?: string;
  reminder_time?: string;
  reminder_note?: string;
  location?: string;
}

export interface VisitPhoto {
  id: string;
  visit_id: string;
  media_link_id: string;
  taken_at: string;
  company_id: string;
  caption?: string;
  created_at: string;
}

export interface VisitNote {
  id: string;
  visit_id: string;
  company_id: string;
  author_user_id?: string;
  content: string;
  note_type: 'general' | 'follow_up' | 'important';
  created_at: string;
}

export interface VisitProduct {
  id: string;
  visit_id: string;
  product_id: string;
  noted_at: string;
  company_id: string;
  notes?: string;
  created_at: string;
}

type VisitRowLike = Record<string, any>;

const normalizeVisitStatus = (rawStatus: string | null | undefined): string | null => {
  if (!rawStatus) return null;
  const normalized = String(rawStatus).toUpperCase();
  if (normalized === 'PENDING_LABEL') return 'in_progress';
  if (normalized === 'LABELED') return 'completed';
  if (normalized === 'IGNORED') return 'cancelled';
  return String(rawStatus).toLowerCase();
};

const toVisitRecord = (row: VisitRowLike): VisitRecord => {
  const metadata = (row.metadata as Record<string, unknown> | null) || {};
  const enteredAt = row.entered_at || row.started_at || row.created_at;
  // Face recognition: has external_id, or has entered_at/exited_at (visits table columns)
  const derivedSource: VisitRecord['source'] =
    row.external_id || row.entered_at || row.exited_at ? 'face_recognition' : 'manual';
  const derivedTemporary =
    !row.customer_id && String(row.status || '').toUpperCase() === 'PENDING_LABEL';

  return {
    id: row.id,
    company_id: row.company_id,
    customer_id: row.customer_id,
    started_at: enteredAt,
    ended_at: row.ended_at || row.exited_at || null,
    duration_sec: row.duration_sec ?? row.visit_duration_sec ?? null,
    source: derivedSource,
    temporary: derivedTemporary,
    visit_type: row.visit_type ?? null,
    notes: row.notes ?? null,
    location: row.camera_id ?? row.location ?? null,
    metadata,
    status: normalizeVisitStatus(row.status),
    created_at: row.created_at,
    updated_at: row.updated_at,
    customer: row.customer || null,
  };
};

export class VisitService {
  /**
   * Add a photo to a visit
   */
  static async addPhotoToVisit(
    companyId: string,
    visitId: string,
    filePath: string,
    bucket: string = 'visit-photos',
    caption?: string
  ): Promise<void> {
    try {
      // First create the media_link record
      const { data: mediaLink, error: mediaError } = await supabase
        .from('media_links')
        .insert({
          company_id: companyId,
          owner_table: 'visits',
          owner_id: visitId,
          bucket: bucket,
          path: filePath,
          mime: 'image/jpeg' // Default, could be detected from file extension
        })
        .select()
        .single();

      if (mediaError) throw mediaError;

      // Then create the visit_photos record
      const { error: visitPhotoError } = await supabase
        .from('visit_photos')
        .insert({
          company_id: companyId,
          visit_id: visitId,
          media_link_id: mediaLink.id,
          caption: caption,
          taken_at: new Date().toISOString()
        });

      if (visitPhotoError) throw visitPhotoError;
    } catch (error) {
      console.error('Error adding photo to visit:', error);
      throw error;
    }
  }

  /**
   * Upload and add photo to visit in one operation
   */
  static async uploadAndAddPhoto(
    companyId: string,
    visitId: string,
    file: File,
    caption?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Import the storage service dynamically to avoid circular dependencies
      const { VisitPhotoService } = await import('@/lib/storage');
      
      // Upload the file
      const uploadResult = await VisitPhotoService.uploadPhoto(companyId, file, visitId);
      
      if (!uploadResult.success) {
        return { success: false, error: uploadResult.error };
      }

      // Add to database
      await this.addPhotoToVisit(companyId, visitId, uploadResult.path, 'visit-photos', caption);

      return { success: true };
    } catch (error) {
      console.error('Error uploading and adding photo:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }
  /**
   * Create a new visit record in the visits table
   */
  static async createVisit(companyId: string, visitData: CreateVisitData): Promise<VisitRecord> {
    // Note: Since the visits table might not exist yet in the current database,
    // we need to check if it exists and create records accordingly
    
    try {
      const visitInsert: Record<string, unknown> = {
        company_id: companyId,
        external_id: crypto.randomUUID(),
        camera_id: visitData.location || 'manual',
        entered_at: visitData.started_at,
        status: 'LABELED',
        customer_id: visitData.customer_id || null,
      };

      const { data: visitRecord, error: visitError } = await (supabase as any)
        .from('visits')
        .insert(visitInsert)
        .select()
        .single();

      if (visitError) {
        throw visitError;
      }

      // Create reminder if specified
      if (visitData.reminder_date) {
        await this.createVisitReminder(
          companyId,
          visitRecord.id,
          visitData.reminder_date,
          visitData.reminder_time,
          visitData.reminder_note
        );
      }

      // Create visit products relationship if product specified
      if (visitData.product_id) {
        await this.addProductToVisit(companyId, visitRecord.id, visitData.product_id);
      }

      return visitRecord;
    } catch (error) {
      // If visits table doesn't exist, fall back to calendar_events approach for now
      console.warn('Visits table not found, falling back to calendar_events:', error);
      
      const insertData = {
        company_id: companyId,
        title: visitData.customer_id 
          ? `Ziyaret - ${visitData.customer_id}` 
          : 'Geçici Ziyaret',
        description: visitData.notes,
        start_date: new Date(visitData.started_at).toISOString().split('T')[0],
        start_time: new Date(visitData.started_at).toTimeString().split(' ')[0],
        end_date: visitData.ended_at ? new Date(visitData.ended_at).toISOString().split('T')[0] : null,
        end_time: visitData.ended_at ? new Date(visitData.ended_at).toTimeString().split(' ')[0] : null,
        event_type: 'visit_record',
        category: 'sales',
        status: visitData.ended_at ? 'completed' : 'in_progress', // Set status based on whether ended_at is provided
        customer_id: visitData.customer_id,
        product_id: visitData.product_id,
        location: visitData.location,
        metadata: {
          visit_type: visitData.visit_type,
          source: visitData.source || 'manual',
          temporary: visitData.temporary || false,
          photos: visitData.photos || [],
          original_started_at: visitData.started_at,
        },
      };

      const { data, error: calendarError } = await supabase
        .from('calendar_events')
        .insert(insertData)
        .select()
        .single();

      if (calendarError) throw calendarError;

      // Create reminder if specified
      if (visitData.reminder_date) {
        await this.createVisitReminder(
          companyId,
          data.id,
          visitData.reminder_date,
          visitData.reminder_time,
          visitData.reminder_note
        );
      }

      // Transform calendar event to visit record format
      return {
        id: data.id,
        company_id: data.company_id,
        customer_id: data.customer_id,
        started_at: data.start_date + 'T' + (data.start_time || '00:00:00'),
        ended_at: data.end_date ? data.end_date + 'T' + (data.end_time || '00:00:00') : null,
        duration_sec: null,
        source: (data.metadata as Record<string, unknown>)?.source || 'manual',
        temporary: (data.metadata as Record<string, unknown>)?.temporary || false,
        visit_type: (data.metadata as Record<string, unknown>)?.visit_type,
        notes: data.description,
        location: data.location,
        metadata: data.metadata,
        status: data.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
    }
  }

  /**
   * Create a reminder for a visit in the calendar_events table
   */
  static async createVisitReminder(
    companyId: string,
    visitId: string,
    reminderDate: string,
    reminderTime?: string,
    reminderNote?: string
  ): Promise<void> {
    const reminderData = {
      company_id: companyId,
      title: `Ziyaret Hatırlatıcısı`,
      description: reminderNote || `Müşteri takibi için hatırlatıcı`,
      start_date: new Date(reminderDate).toISOString().split('T')[0],
      start_time: reminderTime || '09:00:00',
      event_type: 'visit_reminder',
      category: 'sales',
      status: 'scheduled',
      visit_id: visitId,
      metadata: {
        reminder_for_visit: visitId,
      },
    };

    const { error } = await supabase
      .from('calendar_events')
      .insert(reminderData);

    if (error) {
      console.error('Error creating visit reminder:', error);
      throw error;
    }
  }

  /**
   * Add a product to a visit
   */
  static async addProductToVisit(companyId: string, visitId: string, productId: string, notes?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('visit_products')
        .insert({
          company_id: companyId,
          visit_id: visitId,
          product_id: productId,
          noted_at: new Date().toISOString(),
          notes: notes
        });

      if (error) throw error;
    } catch (error) {
      // If visit_products table doesn't exist, we'll store it in metadata for now
      console.warn('visit_products table not found, storing in metadata');
    }
  }

  /**
   * Add a note to a visit
   */
  static async addNoteToVisit(
    companyId: string, 
    visitId: string, 
    content: string, 
    authorUserId?: string, 
    noteType: 'general' | 'follow_up' | 'important' = 'general'
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('visit_notes')
        .insert({
          company_id: companyId,
          visit_id: visitId,
          content: content,
          author_user_id: authorUserId,
          note_type: noteType,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error adding note to visit:', error);
      throw error;
    }
  }

  /**
   * Update a visit's basic information
   */
  static async updateVisit(
    visitId: string, 
    updates: Partial<Pick<VisitRecord, 'notes' | 'visit_type' | 'location'>>
  ): Promise<void> {
    try {
      const { error } = await (supabase as any)
        .from('visits')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', visitId);

      if (error) throw error;
    } catch (error) {
      const { error: calendarError } = await supabase
        .from('calendar_events')
        .update({
          description: updates.notes,
          location: updates.location,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId)
        .eq('event_type', 'visit_record');

      if (calendarError) throw calendarError;
    }
  }

  /**
   * Delete a visit
   */
  static async deleteVisit(visitId: string): Promise<void> {
    try {
      const { error } = await (supabase as any)
        .from('visits')
        .update({ status: 'IGNORED', updated_at: new Date().toISOString() })
        .eq('id', visitId);

      if (error) throw error;
    } catch (error) {
      const { error: calendarError } = await supabase
        .from('calendar_events')
        .update({
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId)
        .eq('event_type', 'visit_record');

      if (calendarError) throw calendarError;
    }
  }

  /**
   * End a visit
   */
  static async endVisit(visitId: string): Promise<void> {
    const endTime = new Date().toISOString();
    
    try {
      const { error } = await (supabase as any)
        .from('visits')
        .update({ status: 'LABELED', updated_at: endTime })
        .eq('id', visitId);

      if (error) throw error;
    } catch (error) {
      const { error: calendarError } = await supabase
        .from('calendar_events')
        .update({
          status: 'completed',
          end_date: endTime.split('T')[0],
          end_time: endTime.split('T')[1].split('.')[0],
          updated_at: endTime,
        })
        .eq('id', visitId)
        .eq('event_type', 'visit_record');

      if (calendarError) throw calendarError;
    }
  }

  /**
   * Get all visits for a company
   */
  static async getVisits(companyId: string): Promise<VisitRecord[]> {
    try {
      // Try visits table first
      let visitsResp = await supabase
        .from('visits')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email)
        `)
        .eq('company_id', companyId)
        .order('entered_at', { ascending: false });

      if (visitsResp.error && String(visitsResp.error.message || '').includes('entered_at')) {
        visitsResp = await supabase
          .from('visits')
          .select(`
            *,
            customer:customers(id, first_name, last_name, email)
          `)
          .eq('company_id', companyId)
          .order('started_at', { ascending: false });
      }
      if (visitsResp.error) throw visitsResp.error;
      return (visitsResp.data || []).map((row) => toVisitRecord(row as VisitRowLike));
    } catch (error) {
      // Fallback to calendar_events
      const { data, error: calendarError } = await supabase
        .from('calendar_events')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email)
        `)
        .eq('company_id', companyId)
        .eq('event_type', 'visit_record')
        .is('deleted_at', null)
        .order('start_date', { ascending: false });

      if (calendarError) throw calendarError;

      // Transform calendar events to visit record format
      return (data || []).map(event =>
        toVisitRecord({
          ...event,
          started_at: event.start_date + 'T' + (event.start_time || '00:00:00'),
          ended_at: event.end_date ? event.end_date + 'T' + (event.end_time || '00:00:00') : null,
        })
      );
    }
  }

  /**
   * Get a single visit by ID
   */
  static async getVisit(visitId: string): Promise<VisitRecord | null> {
    try {
      // Try visits table first
      const { data, error } = await supabase
        .from('visits')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email)
        `)
        .eq('id', visitId)
        .single();

      if (error) throw error;
      return toVisitRecord(data as VisitRowLike);
    } catch (error) {
      // Fallback to calendar_events
      const { data, error: calendarError } = await supabase
        .from('calendar_events')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email)
        `)
        .eq('id', visitId)
        .eq('event_type', 'visit_record')
        .is('deleted_at', null)
        .single();

      if (calendarError) throw calendarError;

      // Transform calendar event to visit record format
      return toVisitRecord({
        ...data,
        started_at: data.start_date + 'T' + (data.start_time || '00:00:00'),
        ended_at: data.end_date ? data.end_date + 'T' + (data.end_time || '00:00:00') : null,
      });
    }
  }

  /**
   * Link a temporary visit to a customer
   */
  static async linkVisitToCustomer(visitId: string, customerId: string): Promise<void> {
    console.log('Attempting to link visit', visitId, 'to customer', customerId);
    
    try {
      const rpcResult = await supabase.rpc('set_visit_customer' as any, {
        p_visit_id: visitId,
        p_customer_id: customerId,
      });

      if (rpcResult.error) {
        console.warn('set_visit_customer RPC failed, using direct update:', rpcResult.error.message);
        const { error } = await (supabase as any)
          .from('visits')
          .update({ customer_id: customerId, status: 'LABELED' })
          .eq('id', visitId);
        if (error) {
          throw new Error(`Failed to update visit record: ${error.message}`);
        }
      }

      const { data: visitFaceRows, error: visitFaceErr } = await supabase
        .from('visit_faces')
        .select('embedding, quality_score, snapshot_path')
        .eq('visit_id', visitId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (visitFaceErr) {
        throw new Error(`Failed to read visit_face embedding: ${visitFaceErr.message}`);
      }
      const face = visitFaceRows?.[0];
      if (face?.embedding) {
        const { error: templateErr } = await supabase
          .from('face_templates')
          .insert({
            customer_id: customerId,
            embedding: face.embedding,
            quality_score: face.quality_score ?? null,
            snapshot_path: face.snapshot_path ?? null,
            source: 'manual_link',
            is_active: true,
          } as any);
        if (templateErr) {
          const code = (templateErr as { code?: string }).code;
          const message = templateErr.message || '';
          const isDuplicate = code === '23505' || /duplicate|already exists|unique/i.test(message);

          if (!isDuplicate) {
            throw new Error(`Failed to save face template after linking: ${templateErr.message}`);
          }

          console.log('Face template already exists for this linked visit, skipping duplicate insert');
        }
      }

      // Log the label action for audit trail
      const labelActionRow: Record<string, unknown> = {
        visit_id: visitId,
        visit_face_id: visitFaceRows?.[0]?.id ?? null,
        action_type: 'ASSIGN_EXISTING',
        customer_id: customerId,
      };
      // Best-effort: don't fail the link if label_actions insert fails
      try {
        await (supabase as any).from('label_actions').insert(labelActionRow);
      } catch (labelErr) {
        console.warn('Failed to log label_action (non-fatal):', labelErr);
      }
    } catch (error) {
      console.error('Error in linkVisitToCustomer:', error);
      throw error;
    }
  }
}
